import moment from "moment";
import DATAMART_SERVICES from "Pages/Contracts/Services/DatamartServices";
import { AgreementField } from "../ContractDetails";
import { AttachmentDetails, AttachmentItem } from "../AgreementAttachments";
import { contractFieldConfig } from "../contractDetailsConfig";
import { apiToFormYesNo } from "../utils/yesNoUtils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ManageAgreementData {
  /** Flat list of agreement detail fields rendered in ContractDetails. */
  agreementFields: AgreementField[];
  /** Attachment files rendered in AgreementAttachments. */
  attachments: AttachmentDetails;
}

export interface ManageAgreementResponse {
  status: boolean;
  data: ManageAgreementData;
  message?: string;
}

// ─── Response Mapping ───────────────────────────────────────────────────────────

/** UI field keys expected by ContractDetails / contractDetailsConfig. */
const CONTRACT_FIELD_KEYS = Object.keys(contractFieldConfig);

/**
 * Explicit mapping of UI form-field key → API response key.
 * Only add entries here as mappings are confirmed; unmapped fields render empty.
 */
const FIELD_KEY_TO_API: Record<string, string> = {
  accountNumber: "account1Star",
  agreementId: "agreementId",
  contractName: "caseName",
  legalEntityNokia: "nokia_legal_entity_name",
  legalEntityCustomer: "customer_legal_entity_name",
  companyId: "companyId",
  keyAccount: "key_account",
  typeOfCompany: "account_type",
  typeOfAgreement: "agreementType",
  territory: "agreementTerritory",
  accountManager: "accountManager",
  requestDate: "request_date",
  deputyOwner: "deputy_market_owner",
  contractStartDate: "validFrom",
  contractEndDate: "validTo",
  currentStatus: "status",
  sourceStatus: "sourceStatus",
  sourceAgreementType: "sourceAgreementType",
  remarks: "remarks",
  partnerSelectionStatus: "partner_selection_process_status",
  validatorEmail: "validatorEmail",
  validatorName: "validatorName",
  previousContractInstance: "previousContractInstance",
  renewalPeriod: "renewalPeriod",
  renewalEnabled: "renewal_enabled",
  renewalNotifications: "renewal_notification",
};

/** Date field format expected by the date picker + shown in view mode. */
const DATE_FORMAT = "YYYY-MM-DD";

/** Normalise any raw value coming from the API into the string the UI renders. */
const toFieldValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(",");
  // Yes/No coercion is radio-only — avoid turning "1"/"0"/"true" into yes/no for text fields.
  if (contractFieldConfig[key]?.type === "radio") {
    const yesNo = apiToFormYesNo(value);
    return yesNo === "yes" || yesNo === "no" ? yesNo : String(value).trim();
  }
  return String(value).trim();
};

/**
 * Format a raw value for a given field key: date fields are normalised to
 * YYYY-MM-DD (the API sends e.g. "2019-02-08 00:00:00"); others pass through.
 */
const formatFieldValue = (key: string, value: string): string => {
  if (!value || contractFieldConfig[key]?.type !== "date") return value;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : value;
};

/**
 * Build the flat AgreementField[] the details grid consumes.
 * Only fields listed in FIELD_KEY_TO_API are populated from the response;
 * every other field resolves to an empty value until its mapping is confirmed.
 */
const buildAgreementFields = (data: Record<string, any>): AgreementField[] => {
  // If the backend already returns the hub-shaped array, use it as-is.
  if (Array.isArray(data?.agreementFields)) {
    return data.agreementFields as AgreementField[];
  }
  return CONTRACT_FIELD_KEYS.map((key) => {
    const apiKey = FIELD_KEY_TO_API[key];
    const value = apiKey ? toFieldValue(key, data?.[apiKey]) : "";
    return {
      key,
      value: formatFieldValue(key, value),
    };
  });
};

/** Turn an API `formElement` (e.g. "agreement_unsigned") into a readable badge. */
const formatBadge = (formElement?: string): string => {
  if (!formElement) return "FA";
  const label = formElement.replace(/^agreement_/, "").replace(/_/g, " ").trim();
  if (!label) return "FA";
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** Map a single `attachedFiles` record into the AttachmentItem shape. */
const mapAttachment = (file: any, index: number): AttachmentItem => ({
  id: String(file?.fileId ?? file?.id ?? `attachment-${index}`),
  badge: formatBadge(file?.formElement),
  fileName: file?.name ?? file?.fileName ?? "",
  startDate: file?.createdAt,
  endDate: file?.updatedAt,
});

/** Build the attachments block from the response's `attachedFiles`. */
const buildAttachments = (data: Record<string, any>): AttachmentDetails => {
  const rawFiles = Array.isArray(data?.attachedFiles) ? data.attachedFiles : [];
  return {
    files: rawFiles.map(mapAttachment),
  };
};

// ─── Service Class ────────────────────────────────────────────────────────────

class ManageAgreementService {
  /**
   * Fetch the agreement detail + attachments for a single agreement selected
   * from the Contract Hub.
   *
   * GET /v2/contract/frame-agreement/<agreement_id>?isContractHub=true
   */
  fetchManageAgreement = async (
    agreementId?: string,
  ): Promise<ManageAgreementResponse> => {
    if (!agreementId) {
      return {
        status: false,
        data: { agreementFields: [], attachments: { files: [] } },
        message: "Missing agreement id",
      };
    }

    const response: any = await DATAMART_SERVICES.genericDatamartAPI({
      method: "get",
      url: `/v2/contract/frame-agreement/${agreementId}?isContractHub=true`,
    });

    if (!response?.status) {
      return {
        status: false,
        data: { agreementFields: [], attachments: { files: [] } },
        message: response?.message || "Failed to fetch agreement details",
      };
    }

    const data = (response?.data as Record<string, any>) || {};

    return {
      status: true,
      data: {
        agreementFields: buildAgreementFields(data),
        attachments: buildAttachments(data),
      },
    };
  };
}

export const manageAgreementService = new ManageAgreementService();
